import { SystemCurrency, getCurrencyConfig, checkUserPaymentCapacity } from '@okletsplay/core';
import { UserBalanceDto } from 'app/interfaces/dto';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { calculatePlatformFeeFromPrimaryAmount } from 'shared/utils';
import { addMethod, number } from 'yup';

export interface UserBalanceValidationOptions {
  getUserAccounts: () => (UserBalanceDto | undefined)[];
  getExchangeRate: () => number;
  getPlatformFeePercentage: () => number;
}

const VALIDATOR_NAME = 'userBalance';

export enum UserBalanceValidationResult {
  Valid = 'Valid',
  InvalidValue = 'InvalidValue',
  AccountNotFound = 'AccountNotFound',
  NotEnoughBalance = 'NotEnoughBalance',
}

const currencyConfig = getCurrencyConfig(MultiCurrencyConfig.currencyType());

export const validateUserBalance = (
  value: number | undefined,
  options: UserBalanceValidationOptions
): { result: UserBalanceValidationResult; details?: { currency: SystemCurrency } } => {
  if (value === undefined) {
    return { result: UserBalanceValidationResult.InvalidValue };
  }

  if (value === 0) {
    return { result: UserBalanceValidationResult.Valid };
  }

  if (!currencyConfig) {
    return { result: UserBalanceValidationResult.InvalidValue };
  }

  const userAccounts = options.getUserAccounts();
  const exchangeRate = options.getExchangeRate();

  const mainAccount = userAccounts.find((account) => account?.currency === currencyConfig.main);

  if (!mainAccount) {
    return {
      result: UserBalanceValidationResult.AccountNotFound,
      details: { currency: currencyConfig.main },
    };
  }

  if (!currencyConfig.platformFee) {
    if (mainAccount.balance >= value) {
      return { result: UserBalanceValidationResult.Valid };
    } else {
      return {
        result: UserBalanceValidationResult.NotEnoughBalance,
        details: { currency: currencyConfig.main },
      };
    }
  }

  const platformFeeAccount = userAccounts.find(
    (account) => account?.currency === currencyConfig.platformFee
  );

  if (!platformFeeAccount) {
    return {
      result: UserBalanceValidationResult.AccountNotFound,
      details: { currency: currencyConfig.platformFee },
    };
  }

  const platformFeeAmount = calculatePlatformFeeFromPrimaryAmount(
    value,
    exchangeRate,
    options.getPlatformFeePercentage()
  );

  const accountValidationsByCurrency = checkUserPaymentCapacity(
    value,
    platformFeeAmount,
    MultiCurrencyConfig.currencyType(),
    userAccounts
  );

  const notValidResult = accountValidationsByCurrency.find((validation) => !validation.isValid);

  if (notValidResult) {
    switch (notValidResult.currency) {
      case currencyConfig.platformFee:
      case currencyConfig.main: {
        return {
          result: UserBalanceValidationResult.NotEnoughBalance,
          details: { currency: notValidResult.currency },
        };
      }
      default: {
        return { result: UserBalanceValidationResult.InvalidValue };
      }
    }
  }

  return { result: UserBalanceValidationResult.Valid };
};

addMethod(number, VALIDATOR_NAME, function (options: UserBalanceValidationOptions) {
  return this.test(VALIDATOR_NAME, function (value, context) {
    const validation = validateUserBalance(value, options);

    switch (validation.result) {
      case UserBalanceValidationResult.Valid:
        return true;
      case UserBalanceValidationResult.AccountNotFound:
      case UserBalanceValidationResult.NotEnoughBalance: {
        const currency = validation.details?.currency;
        const message = ['Not enough', currency, 'balance'].filter(Boolean).join(' ');
        throw context.createError({ message });
      }
      case UserBalanceValidationResult.InvalidValue:
      default:
        return false;
    }
  });
});
